import React from "react";

import type { SubmenuContentBlock } from "@reactivated";

import { LinkList, normalizeLinks } from "./LinkList";
import { NavAd } from "./NavAd";

import styles from "./SubmenuContent.module.scss";

interface Props {
    items: SubmenuContentBlock;
}

const buildSpacer = (items: SubmenuContentBlock) => {
    const totalCols = items.reduce(
        (sum, item) => sum + (Number(item.value.column_width) || 4),
        0,
    );
    const remainingCols = 12 - totalCols;

    if (items.length === 0 || remainingCols <= 0)
        return { index: null, element: null };

    // Backend validation guarantees lists are grouped together, so find the
    // boundary where the type changes from one group to the other.
    const firstItemIsAd = items[0].type === "nav_ad";
    // Fancy one-liner to find the first item whose type differs from the first item's type.
    const boundary = items.findIndex(
        (item) => (item.type === "nav_ad") !== firstItemIsAd,
    );
    // Returns -1 if all items are the same type, meaning no spacer needed
    if (boundary === -1) return { index: null, element: null };

    return {
        index: boundary,
        element: (
            <li
                key="spacer"
                className={styles.spacer}
                style={{ "--desktop-layout-col-span": remainingCols }}
                aria-hidden="true"
            />
        ),
    };
};

export const SubmenuContent = ({ items }: Props) => {
    const spacer = buildSpacer(items);

    return (
        <ul className={styles.grid}>
            {items.map((item, index) => {
                const style = {
                    "--desktop-layout-col-span":
                        Number(item.value.column_width) || 4,
                    "--desktop-layout-col-span-narrow":
                        item.type === "nav_ad" && !item.value.full_width_mobile
                            ? 2
                            : 4,
                };

                switch (item.type) {
                    case "link_list":
                    case "link_list_page_tree":
                    case "link_list_facet":
                        if (!item.value.links) return null;
                        return (
                            <React.Fragment key={item.id}>
                                {index === spacer.index && spacer.element}
                                <li className={styles.column} style={style}>
                                    <LinkList
                                        id={item.id}
                                        title={item.value.title ?? null}
                                        links={normalizeLinks(item)}
                                    />
                                </li>
                            </React.Fragment>
                        );
                    case "nav_ad":
                        return (
                            <React.Fragment key={item.id}>
                                {index === spacer.index && spacer.element}
                                <li className={styles.column} style={style}>
                                    <NavAd item={item.value} />
                                </li>
                            </React.Fragment>
                        );
                }
            })}
        </ul>
    );
};
